/**
 * The editor's local copy of changes the server has not confirmed yet.
 *
 * Every change is kept here (IndexedDB) until a save of it is confirmed, so
 * closing the editor, leaving the page, switching apps or locking the phone
 * mid-save loses nothing: the next time the lesson opens, a copy newer than
 * the stored draft is put back on screen and saved. Recordings that were never
 * uploaded are kept as blobs alongside it.
 *
 * Best effort: IndexedDB can be missing or throw (private mode, storage
 * pressure), and every call here then does nothing.
 */

export interface PendingClip {
  id: string;
  videoStoragePath?: string;
  videoBlob?: Blob;
}

export interface PendingPlan {
  voiceoverNotableSegmentClips: PendingClip[];
}

export interface PendingDraft<T extends PendingPlan> {
  data: T;
  /** When the newest change in `data` was made (ISO). */
  editedAt: string;
}

/**
 * Whether a local copy should replace the stored draft on open: only when it
 * holds a change made after the stored draft was last written. A save that
 * included the change stamps the draft later than the change, so a copy whose
 * save did land is discarded rather than replayed.
 */
export function shouldApplyPending(pendingEditedAt: string, storedLastEditedAt?: string): boolean {
  return !storedLastEditedAt || pendingEditedAt > storedLastEditedAt;
}

/** The plan without blobs, plus the recordings that only exist in memory. */
export function splitBlobs<T extends PendingPlan>(data: T): { plan: T; blobs: [string, Blob][] } {
  const blobs: [string, Blob][] = [];
  const clips = data.voiceoverNotableSegmentClips.map((clip) => {
    if (!clip.videoBlob) return clip;
    if (!clip.videoStoragePath) blobs.push([clip.id, clip.videoBlob]);
    const rest = { ...clip };
    delete rest.videoBlob;
    return rest;
  });
  return { plan: { ...data, voiceoverNotableSegmentClips: clips }, blobs };
}

const DB_NAME = 'db-editor-pending';
const DRAFTS = 'drafts';
const BLOBS = 'blobs';
let dbPromise: Promise<IDBDatabase> | null = null;
/** Blobs already written this session, so a take is stored once rather than on every change. */
const storedBlobs = new Set<string>();

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(DRAFTS);
        req.result.createObjectStore(BLOBS);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}

function run<R>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<R> | void): Promise<R | undefined> {
  return openDb().then((db) => new Promise<R | undefined>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = fn(tx.objectStore(store));
    tx.oncomplete = () => resolve(req ? req.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));
}

const blobKey = (key: string, clipId: string) => `${key}|${clipId}`;

export async function savePending<T extends PendingPlan>(key: string, data: T, editedAt: string): Promise<void> {
  try {
    const { plan, blobs } = splitBlobs(data);
    await run(DRAFTS, 'readwrite', (s) => s.put({ data: plan, editedAt }, key));
    for (const [clipId, blob] of blobs) {
      const k = blobKey(key, clipId);
      if (storedBlobs.has(k)) continue;
      try {
        await run(BLOBS, 'readwrite', (s) => s.put(blob, k));
        storedBlobs.add(k);
      } catch (err) {
        console.warn('[pendingDraft] could not keep a recording locally', err);
      }
    }
  } catch (err) {
    console.warn('[pendingDraft] could not keep a local copy', err);
  }
}

/** The local copy for `key` with its recordings reattached, or null. */
export async function readPending<T extends PendingPlan>(key: string): Promise<PendingDraft<T> | null> {
  try {
    const rec = await run<PendingDraft<T> | undefined>(DRAFTS, 'readonly', (s) => s.get(key));
    if (!rec) return null;
    const clips: PendingClip[] = [];
    for (const clip of rec.data.voiceoverNotableSegmentClips) {
      if (clip.videoStoragePath) { clips.push(clip); continue; }
      const blob = await run<Blob | undefined>(BLOBS, 'readonly', (s) => s.get(blobKey(key, clip.id)));
      // A take with no file and no upload cannot be played or saved: leave it out.
      if (blob) clips.push({ ...clip, videoBlob: blob });
    }
    return { ...rec, data: { ...rec.data, voiceoverNotableSegmentClips: clips } };
  } catch (err) {
    console.warn('[pendingDraft] could not read the local copy', err);
    return null;
  }
}

/**
 * Drops the local copy once a save of it is confirmed. A copy holding a later
 * change than the confirmed save (`editedAt`) is kept.
 */
export async function clearPending(key: string, editedAt: string): Promise<void> {
  try {
    const db = await openDb();
    // One transaction, so a newer copy written meanwhile cannot be dropped.
    const cleared = await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction([DRAFTS, BLOBS], 'readwrite');
      let done = false;
      const get = tx.objectStore(DRAFTS).get(key);
      get.onsuccess = () => {
        const rec = get.result as PendingDraft<PendingPlan> | undefined;
        if (rec && rec.editedAt > editedAt) return;
        tx.objectStore(DRAFTS).delete(key);
        tx.objectStore(BLOBS).delete(IDBKeyRange.bound(`${key}|`, `${key}|￿`));
        done = true;
      };
      tx.oncomplete = () => resolve(done);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    if (cleared) for (const k of [...storedBlobs]) if (k.startsWith(`${key}|`)) storedBlobs.delete(k);
  } catch (err) {
    console.warn('[pendingDraft] could not clear the local copy', err);
  }
}
